import { useFilePlates } from '../api';
import { Package, Layers, Palette, AlertCircle, Clock, Printer } from 'lucide-react';
import type { ModelFile } from '@maker-toolchest/shared';

interface FileMetadataDisplayProps {
  modelId: string;
  file: ModelFile;
}

export function FileMetadataDisplay({ modelId, file }: FileMetadataDisplayProps) {
  const { data: platesResponse, isLoading } = useFilePlates(
    modelId,
    file.id,
    file.metadataStatus === 'COMPLETED'
  );

  // Show metadata extraction status if not completed
  if (file.metadataStatus === 'PENDING' || file.metadataStatus === 'PROCESSING') {
    return (
      <div className="flex items-center gap-2 text-xs text-foreground-muted">
        <Clock className="h-4 w-4" />
        <span>
          {file.metadataStatus === 'PENDING' ? 'Metadata extraction queued' : 'Extracting metadata...'}
        </span>
      </div>
    );
  }

  if (file.metadataStatus === 'FAILED') {
    return (
      <div className="flex items-center gap-2 text-xs text-error">
        <AlertCircle className="h-4 w-4" />
        <span>Metadata extraction failed</span>
      </div>
    );
  }

  const plates = platesResponse?.plates || [];
  const metadata = platesResponse?.metadata;

  // If no plates data or still loading, show basic info
  if (isLoading || !plates || plates.length === 0) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-foreground-muted">
        <div>
          <div className="font-medium">File Type</div>
          <div className="uppercase">{file.fileType || 'STL'}</div>
        </div>
        {file.volumeMm3 && (
          <div>
            <div className="font-medium">Volume</div>
            <div>{formatVolume(file.volumeMm3)}</div>
          </div>
        )}
        {file.boundingBoxX && file.boundingBoxY && file.boundingBoxZ && (
          <div>
            <div className="font-medium">Dimensions</div>
            <div>
              {Math.round(file.boundingBoxX)} × {Math.round(file.boundingBoxY)} × {Math.round(file.boundingBoxZ)} mm
            </div>
          </div>
        )}
      </div>
    );
  }

  // Calculate metadata from plates
  const totalObjects = plates.reduce((sum, plate) => sum + (plate.objects?.length || 0), 0);
  const uniqueColors = new Set(
    plates.flatMap(plate =>
      plate.objects?.map((obj: { colorHex: string | null }) => obj.colorHex).filter(Boolean) || []
    )
  );
  const hasMultipleColors = uniqueColors.size > 1;

  // Get all unique object names
  const objectNames = plates
    .flatMap(plate => plate.objects?.map((obj: { name: string | null }) => obj.name).filter(Boolean) || [])
    .filter((name, index, arr) => arr.indexOf(name) === index);

  return (
    <div className="space-y-3">
      {/* Primary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-foreground-muted">
        <div>
          <div className="font-medium">File Type</div>
          <div className="uppercase">{file.fileType || 'STL'}</div>
        </div>

        <div>
          <div className="font-medium flex items-center gap-1">
            <Layers className="h-3 w-3" />
            Build Plates
          </div>
          <div>{plates.length}</div>
        </div>

        <div>
          <div className="font-medium flex items-center gap-1">
            <Package className="h-3 w-3" />
            Objects
          </div>
          <div>{totalObjects}</div>
        </div>

        {hasMultipleColors && (
          <div>
            <div className="font-medium flex items-center gap-1">
              <Palette className="h-3 w-3" />
              Colors
            </div>
            <div className="flex items-center gap-1">
              <span>{uniqueColors.size}</span>
              {Array.from(uniqueColors).slice(0, 3).map((color, i) => (
                <div
                  key={i}
                  className="w-3 h-3 rounded-full border border-border"
                  style={{ backgroundColor: color as string }}
                  title={color as string}
                />
              ))}
              {uniqueColors.size > 3 && (
                <span className="text-foreground-muted">+{uniqueColors.size - 3}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Printer and filament info from file metadata */}
      {metadata && (metadata.printerModel || metadata.filamentColors.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          {metadata.printerModel && (
            <div>
              <div className="font-medium text-foreground-muted flex items-center gap-1">
                <Printer className="h-3 w-3" />
                Printer Model
              </div>
              <div className="text-foreground">{metadata.printerModel}</div>
              {metadata.compatiblePrinters.length > 0 && (
                <div className="text-foreground-muted mt-1">
                  Compatible: {metadata.compatiblePrinters.length} printer{metadata.compatiblePrinters.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}

          {metadata.filamentColors.length > 0 && (
            <div>
              <div className="font-medium text-foreground-muted flex items-center gap-1">
                <Palette className="h-3 w-3" />
                Filament Colors
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {metadata.filamentColors.map((color, i) => (
                  <div
                    key={i}
                    className="w-4 h-4 rounded-full border border-border"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Object names (if not too many) */}
      {objectNames.length > 0 && objectNames.length <= 5 && (
        <div className="text-xs">
          <div className="font-medium text-foreground-muted mb-1">Objects:</div>
          <div className="flex flex-wrap gap-1">
            {objectNames.map((name, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-background-subtle rounded text-foreground-muted"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Plate details (if multiple plates) */}
      {plates.length > 1 && (
        <div className="text-xs">
          <div className="font-medium text-foreground-muted mb-1">Plates:</div>
          <div className="space-y-1">
            {plates.map((plate) => (
              <div key={plate.id} className="flex items-center gap-2">
                <span className="text-foreground-muted">
                  Plate {plate.plateNumber}:
                </span>
                <span className="text-foreground">
                  {plate.objects?.length || 0} object{plate.objects?.length !== 1 ? 's' : ''}
                </span>
                {plate.name && (
                  <span className="text-foreground-muted">({plate.name})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to format volume
function formatVolume(volumeMm3: number): string {
  if (volumeMm3 < 1000) {
    return `${volumeMm3.toFixed(1)} mm³`;
  }
  const volumeCm3 = volumeMm3 / 1000;
  return `${volumeCm3.toFixed(2)} cm³`;
}
